import React from 'react';
import { useOutletContext } from 'react-router-dom';

import type { SiteDetailContext } from './SiteRoute';
import { useSiteAnalytics } from '../hooks/useSiteAnalytics';

export const SiteDetailAnalytics: React.FC = () => {
  const { site } = useOutletContext<SiteDetailContext>();
  const { data: analytics, error: analyticsError } = useSiteAnalytics(site.id, { days: 30 });

  const fmtNum = (n: number) => n.toLocaleString();
  const series = analytics?.series ?? [];
  const seriesMax = series.reduce((m, p) => (p.pageViews > m ? p.pageViews : m), 0);
  const barHeight = (views: number) => {
    if (seriesMax <= 0) return '20%';
    const pct = Math.max(8, Math.round((views / seriesMax) * 100));
    return `${pct}%`;
  };
  const bars = series.length === 30
    ? series
    : Array.from({ length: 30 }, (_, i) => ({ date: String(i), pageViews: 0 }));
  const pageViews = analytics ? fmtNum(analytics.pageViews) : '0';
  const visitors = analytics ? fmtNum(analytics.visitors) : '0';

  return (
    <div className="mb-12">
      <div className="rounded-2xl bg-foreground/5 text-foreground p-6 flex flex-col gap-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">
              Page views
            </span>
            <span className="text-2xl font-medium tracking-tight">{pageViews}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">
              Visitors
            </span>
            <span className="text-2xl font-medium tracking-tight">{visitors}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">
              Avg. session
            </span>
            <span className="text-2xl font-medium tracking-tight">—</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">
              Traffic
            </span>
            {/* Daily traffic sparkline — one bar per day, height normalized
                to the window's max so traffic shape stays readable. */}
            <div className="flex items-end gap-1 h-7" aria-hidden="true">
              {bars.map((p, i) => (
                <div
                  key={p.date + i}
                  className="flex-1 rounded-sm bg-foreground/10"
                  style={{ height: barHeight(p.pageViews) }}
                />
              ))}
            </div>
          </div>
        </div>

        {analyticsError && (
          <p className="text-sm text-muted-foreground">Couldn&apos;t load analytics — try again later.</p>
        )}
      </div>
    </div>
  );
};

export default SiteDetailAnalytics;
